// utils.ts - Complete utility functions for all 6 sections (Fixed for Material-UI v5)

import React from 'react';
import { Chip } from "@mui/material";
import { 
  CheckCircle, 
  Error as ErrorIcon, 
  Warning, 
  Timeline, 
  Schedule,
  Code
} from "@mui/icons-material";

// ============================================================================
// DATE FORMATTING UTILITIES
// ============================================================================

export const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
};

export const formatDateShort = (dateString: string | null): string => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

// ============================================================================
// NUMBER AND SIZE FORMATTING UTILITIES
// ============================================================================

export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat().format(num);
};

export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${Math.round(minutes)}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  return `${hours}h ${remainingMinutes}m`;
};

export const formatHours = (hours: number): string => {
  if (hours < 24) {
    return `${Math.round(hours)}h`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = Math.round(hours % 24);
  return `${days}d ${remainingHours}h`;
};

// ============================================================================
// REPOSITORY STATUS UTILITIES
// ============================================================================

export const getHealthStatusIcon = (status: string) => {
  switch (status) {
    case 'healthy':
      return React.createElement(CheckCircle, { sx: { color: '#4caf50', fontSize: 18 } });
    case 'warning':
      return React.createElement(Warning, { sx: { color: '#ff9800', fontSize: 18 } });
    case 'stale':
      return React.createElement(ErrorIcon, { sx: { color: '#f44336', fontSize: 18 } });
    default:
      return React.createElement(Warning, { sx: { color: '#ff9800', fontSize: 18 } });
  }
};

export const getHealthStatusChip = (status: string) => {
  const chipProps = {
    healthy: { label: 'Healthy', sx: { backgroundColor: '#4caf50', color: 'white' } },
    warning: { label: 'Warning', sx: { backgroundColor: '#ff9800', color: 'white' } },
    stale: { label: 'Stale', sx: { backgroundColor: '#f44336', color: 'white' } }
  };
  
  const props = chipProps[status as keyof typeof chipProps] || chipProps.warning;
  return React.createElement(Chip, { size: "small", ...props });
};

export const getHealthStatusColor = (status: string): string => {
  switch (status) {
    case 'healthy':
      return '#4caf50';
    case 'warning':
      return '#ff9800';
    case 'stale':
      return '#f44336';
    default:
      return '#9e9e9e';
  }
};

export const getPRStatusColor = (status: string): string => {
  switch (status) {
    case 'open':
      return '#2196f3';
    case 'merged':
      return '#4caf50';
    case 'closed':
      return '#f44336';
    default:
      return '#9e9e9e';
  }
};

// ============================================================================
// PIPELINE STATUS UTILITIES
// ============================================================================

export const getBuildStatusColor = (status: string): string => {
  switch (status) {
    case 'succeeded':
      return '#4caf50';
    case 'failed':
      return '#f44336';
    case 'running':
      return '#2196f3';
    case 'canceled':
      return '#ff9800';
    case 'partiallySucceeded':
      return '#ff9800';
    default:
      return '#9e9e9e';
  }
};

export const getStatusIcon = (status: string) => {
  switch (status) {
    case 'succeeded':
      return React.createElement(CheckCircle, { sx: { color: '#4caf50', fontSize: 18 } });
    case 'failed':
      return React.createElement(ErrorIcon, { sx: { color: '#f44336', fontSize: 18 } });
    case 'running':
      return React.createElement(Timeline, { sx: { color: '#2196f3', fontSize: 18 } });
    default:
      return React.createElement(Warning, { sx: { color: '#ff9800', fontSize: 18 } });
  }
};

export const getStatusChip = (status: string) => {
  const colors = {
    succeeded: { bg: '#4caf50', color: 'white' },
    failed: { bg: '#f44336', color: 'white' },
    running: { bg: '#2196f3', color: 'white' },
    disabled: { bg: '#9e9e9e', color: 'white' }
  };
  const color = colors[status as keyof typeof colors] || colors.disabled;
  return React.createElement(Chip, { size: "small", label: status, sx: { backgroundColor: color.bg, color: color.color } });
};

// ============================================================================
// RELEASE STATUS UTILITIES
// ============================================================================

export const getReleaseStatusColor = (status: string): string => {
  switch (status) {
    case 'succeeded':
      return '#4caf50';
    case 'failed':
      return '#f44336';
    case 'inProgress':
      return '#2196f3';
    case 'canceled':
      return '#ff9800';
    case 'partiallySucceeded':
      return '#ff9800';
    case 'rejected':
      return '#9c27b0';
    case 'pending':
      return '#607d8b';
    case 'approved':
      return '#4caf50';
    case 'active':
      return '#2196f3';
    case 'draft':
      return '#9e9e9e';
    case 'abandoned':
      return '#f44336';
    default:
      return '#9e9e9e';
  }
};

export const getReleaseStatusIcon = (status: string) => {
  switch (status) {
    case 'succeeded':
      return React.createElement(CheckCircle, { sx: { color: '#4caf50', fontSize: 18 } });
    case 'failed':
      return React.createElement(ErrorIcon, { sx: { color: '#f44336', fontSize: 18 } });
    case 'inProgress':
      return React.createElement(Timeline, { sx: { color: '#2196f3', fontSize: 18 } });
    case 'canceled':
      return React.createElement(Warning, { sx: { color: '#ff9800', fontSize: 18 } });
    case 'active':
      return React.createElement(CheckCircle, { sx: { color: '#2196f3', fontSize: 18 } });
    case 'draft':
      return React.createElement(Schedule, { sx: { color: '#9e9e9e', fontSize: 18 } });
    default:
      return React.createElement(Warning, { sx: { color: '#ff9800', fontSize: 18 } });
  }
};

export const getReleaseStatusChip = (status: string) => {
  const color = getReleaseStatusColor(status);
  return React.createElement(Chip, { size: "small", label: status, sx: { backgroundColor: color, color: 'white' } });
};

export const getEnvironmentStatusIcon = (status: string) => {
  switch (status) {
    case 'succeeded':
      return React.createElement(CheckCircle, { sx: { color: '#4caf50', fontSize: 16 } });
    case 'failed':
      return React.createElement(ErrorIcon, { sx: { color: '#f44336', fontSize: 16 } });
    case 'inProgress':
      return React.createElement(Timeline, { sx: { color: '#2196f3', fontSize: 16 } });
    case 'canceled':
      return React.createElement(Warning, { sx: { color: '#ff9800', fontSize: 16 } });
    case 'queued':
      return React.createElement(Schedule, { sx: { color: '#607d8b', fontSize: 16 } });
    case 'notStarted':
      return React.createElement(Schedule, { sx: { color: '#9e9e9e', fontSize: 16 } });
    default:
      return React.createElement(Warning, { sx: { color: '#ff9800', fontSize: 16 } });
  }
};

export const getAutomergeStatusColor = (status: string): string => {
  switch (status) {
    case 'failed':
      return '#f44336';
    case 'retrying':
      return '#ff9800';
    case 'resolved':
      return '#4caf50';
    case 'abandoned':
      return '#9e9e9e';
    default:
      return '#9e9e9e';
  }
};

// ============================================================================
// BRANCH POLICY COMPLIANCE UTILITIES
// ============================================================================

export const getComplianceColor = (status: string): string => {
  switch (status) {
    case 'compliant':
      return '#4caf50';
    case 'non_compliant':
      return '#f44336';
    case 'partial':
      return '#ff9800';
    case 'unknown':
      return '#9e9e9e';
    default:
      return '#9e9e9e';
  }
};

export const getComplianceIcon = (status: string) => {
  switch (status) {
    case 'compliant':
      return React.createElement(CheckCircle, { sx: { color: '#4caf50', fontSize: 18 } });
    case 'non_compliant':
      return React.createElement(ErrorIcon, { sx: { color: '#f44336', fontSize: 18 } });
    case 'partial':
      return React.createElement(Warning, { sx: { color: '#ff9800', fontSize: 18 } });
    case 'unknown':
      return React.createElement(Warning, { sx: { color: '#9e9e9e', fontSize: 18 } });
    default:
      return React.createElement(Warning, { sx: { color: '#9e9e9e', fontSize: 18 } });
  }
};

export const getComplianceChip = (status: string) => {
  const color = getComplianceColor(status);
  const label = status === 'non_compliant' ? 'Non-Compliant' : 
                status === 'compliant' ? 'Compliant' : 
                status === 'partial' ? 'Partial' : 'Unknown';
  return React.createElement(Chip, { size: "small", label, sx: { backgroundColor: color, color: 'white' } });
};

export const getComplianceLabel = (status: string): string => {
  switch (status) {
    case 'compliant':
      return 'Compliant';
    case 'non_compliant':
      return 'Non-Compliant';
    case 'partial':
      return 'Partial';
    case 'unknown':
      return 'Unknown';
    default:
      return 'Unknown';
  }
};

// ============================================================================
// DEPLOYMENT STATUS UTILITIES
// ============================================================================

export const getDeploymentStatusColor = (status: string): string => {
  switch (status) {
    case 'completed':
      return '#4caf50';
    case 'failed':
      return '#f44336';
    case 'in_progress':
      return '#2196f3';
    case 'cancelled':
      return '#ff9800';
    case 'on_hold':
      return '#9c27b0';
    case 'planned':
      return '#607d8b';
    case 'pending_approval':
      return '#ff5722';
    default:
      return '#9e9e9e';
  }
};

export const getDeploymentStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return React.createElement(CheckCircle, { sx: { color: '#4caf50', fontSize: 18 } });
    case 'failed':
      return React.createElement(ErrorIcon, { sx: { color: '#f44336', fontSize: 18 } });
    case 'in_progress':
      return React.createElement(Timeline, { sx: { color: '#2196f3', fontSize: 18 } });
    case 'cancelled':
      return React.createElement(Warning, { sx: { color: '#ff9800', fontSize: 18 } });
    case 'on_hold':
      return React.createElement(Schedule, { sx: { color: '#9c27b0', fontSize: 18 } });
    case 'planned':
      return React.createElement(Schedule, { sx: { color: '#607d8b', fontSize: 18 } });
    default:
      return React.createElement(Warning, { sx: { color: '#9e9e9e', fontSize: 18 } });
  }
};

export const getDeploymentStatusChip = (status: string) => {
  const color = getDeploymentStatusColor(status);
  const label = getDeploymentStatusLabel(status);
  return React.createElement(Chip, { size: "small", label, sx: { backgroundColor: color, color: 'white' } });
};

export const getDeploymentStatusLabel = (status: string): string => {
  switch (status) {
    case 'completed':
      return 'COMPLETED';
    case 'failed':
      return 'FAILED';
    case 'in_progress':
      return 'IN PROGRESS';
    case 'cancelled':
      return 'CANCELLED';
    case 'on_hold':
      return 'ON HOLD';
    case 'planned':
      return 'PLANNED';
    case 'pending_approval':
      return 'PENDING APPROVAL';
    default:
      return status.toUpperCase();
  }
};

// ============================================================================
// BRANCH NAMING UTILITIES
// ============================================================================

export const getBranchNamingStatusColor = (status: string): string => {
  switch (status) {
    case 'compliant':
      return '#4caf50';
    case 'non_compliant':
      return '#f44336';
    case 'unknown':
      return '#9e9e9e';
    default:
      return '#9e9e9e';
  }
};

export const getBranchNamingStatusIcon = (status: string) => {
  switch (status) {
    case 'compliant':
      return React.createElement(CheckCircle, { sx: { color: '#4caf50', fontSize: 18 } });
    case 'non_compliant':
      return React.createElement(ErrorIcon, { sx: { color: '#f44336', fontSize: 18 } });
    case 'unknown':
      return React.createElement(Warning, { sx: { color: '#9e9e9e', fontSize: 18 } });
    default:
      return React.createElement(Warning, { sx: { color: '#9e9e9e', fontSize: 18 } });
  }
};

export const getBranchNamingStatusChip = (status: string) => {
  const color = getBranchNamingStatusColor(status);
  const label = status === 'non_compliant' ? 'Non-Compliant' : 
                status === 'compliant' ? 'Compliant' : 'Unknown';
  return React.createElement(Chip, { size: "small", label, sx: { backgroundColor: color, color: 'white' } });
};

export const getBranchViolationSeverityColor = (severity: string): string => {
  switch (severity) {
    case 'high':
      return '#f44336';
    case 'medium':
      return '#ff9800';
    case 'low':
      return '#2196f3';
    default:
      return '#9e9e9e';
  }
};

export const getBranchViolationTypeColor = (violationType: string): string => {
  switch (violationType) {
    case 'uppercase_letters':
      return '#f44336'; // High priority - red
    case 'special_characters':
      return '#ff9800'; // Medium priority - orange
    case 'excessive_length':
      return '#ff9800'; // Medium priority - orange
    case 'consecutive_separators':
      return '#2196f3'; // Low priority - blue
    case 'leading_trailing_separators':
      return '#2196f3'; // Low priority - blue
    default:
      return '#9e9e9e';
  }
};

export const getBranchViolationIcon = (violationType: string) => {
  const color = getBranchViolationTypeColor(violationType);
  
  switch (violationType) {
    case 'uppercase_letters':
      return React.createElement(ErrorIcon, { sx: { color, fontSize: 16 } });
    case 'special_characters':
      return React.createElement(Warning, { sx: { color, fontSize: 16 } });
    case 'excessive_length':
      return React.createElement(Code, { sx: { color, fontSize: 16 } });
    case 'consecutive_separators':
      return React.createElement(Warning, { sx: { color, fontSize: 16 } });
    case 'leading_trailing_separators':
      return React.createElement(Warning, { sx: { color, fontSize: 16 } });
    default:
      return React.createElement(Warning, { sx: { color, fontSize: 16 } });
  }
};

export const getBranchViolationChip = (violationType: string, severity?: string) => {
  const color = severity ? getBranchViolationSeverityColor(severity) : getBranchViolationTypeColor(violationType);
  
  const typeLabels = {
    'uppercase_letters': 'Uppercase',
    'special_characters': 'Special Chars',
    'excessive_length': 'Too Long',
    'consecutive_separators': 'Consecutive',
    'leading_trailing_separators': 'Leading/Trailing'
  };
  
  const label = typeLabels[violationType as keyof typeof typeLabels] || violationType;
  
  return React.createElement(Chip, { size: "small", label, sx: { backgroundColor: color, color: 'white' } });
};

// Helper function to validate branch names
export const validateBranchName = (branchName: string): {
  isValid: boolean;
  violations: Array<{
    type: string;
    message: string;
    severity: 'high' | 'medium' | 'low';
  }>;
} => {
  const violations = [];
  
  // Check for uppercase letters
  if (/[A-Z]/.test(branchName)) {
    violations.push({
      type: 'uppercase_letters',
      message: 'Branch name contains uppercase letters',
      severity: 'high' as const
    });
  }
  
  // Check for excessive length
  if (branchName.length > 100) {
    violations.push({
      type: 'excessive_length',
      message: `Branch name is too long (${branchName.length} characters)`,
      severity: 'medium' as const
    });
  }
  
  // Check for problematic special characters
  if (/[^a-zA-Z0-9\-_\/\.]/.test(branchName)) {
    violations.push({
      type: 'special_characters',
      message: 'Branch name contains problematic special characters',
      severity: 'medium' as const
    });
  }
  
  // Check for consecutive separators
  if (/[-_]{2,}|\/\//.test(branchName)) {
    violations.push({
      type: 'consecutive_separators',
      message: 'Branch name contains consecutive separators',
      severity: 'low' as const
    });
  }
  
  // Check for leading/trailing separators
  if (/^[-_\/]|[-_\/]$/.test(branchName)) {
    violations.push({
      type: 'leading_trailing_separators',
      message: 'Branch name starts or ends with a separator',
      severity: 'low' as const
    });
  }
  
  return {
    isValid: violations.length === 0,
    violations
  };
};

// Helper function to suggest branch name fixes
export const suggestBranchNameFix = (branchName: string): string => {
  let fixedName = branchName;
  
  // Convert to lowercase
  fixedName = fixedName.toLowerCase();
  
  // Remove problematic special characters (keep only letters, numbers, hyphens, underscores, slashes, dots)
  fixedName = fixedName.replace(/[^a-z0-9\-_\/\.]/g, '-');
  
  // Replace consecutive separators with single ones
  fixedName = fixedName.replace(/[-_]{2,}/g, '-');
  fixedName = fixedName.replace(/\/\/+/g, '/');
  
  // Remove leading and trailing separators
  fixedName = fixedName.replace(/^[-_\/]+|[-_\/]+$/g, '');
  
  // Truncate if too long
  if (fixedName.length > 100) {
    fixedName = fixedName.substring(0, 100).replace(/[-_\/\.]+$/, '');
  }
  
  return fixedName;
};

// ============================================================================
// BACKEND CONNECTION UTILITIES
// ============================================================================

export const testBackendConnection = async (): Promise<string | null> => {
  const testUrls = [
    '/api/ado/health',
    'http://localhost:7007/api/ado/health'
  ];
  
  for (const testUrl of testUrls) {
    try {
      console.log(`[FRONTEND] Testing backend connection at: ${testUrl}`);
      
      const healthResponse = await fetch(testUrl);
      console.log(`[FRONTEND] Health response status for ${testUrl}:`, healthResponse.status);
      
      if (healthResponse.ok) {
        const healthData = await healthResponse.json();
        console.log('[FRONTEND] Health check successful:', healthData);
        
        const debugUrl = testUrl.replace('/health', '/debug/routes');
        const debugResponse = await fetch(debugUrl);
        if (debugResponse.ok) {
          const debugData = await debugResponse.json();
          console.log('[FRONTEND] Available routes:', debugData);
        }
        
        return testUrl.startsWith('http') ? 'http://localhost:7007' : '';
      }
    } catch (error) {
      console.error(`[FRONTEND] Backend connection test failed for ${testUrl}:`, error);
    }
  }
  
  return null;
};

// ============================================================================
// API CALL UTILITIES
// ============================================================================

export const makeApiCall = async (
  url: string, 
  method: string = 'GET', 
  body?: any
): Promise<any> => {
  const options: RequestInit = {
    method,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
  };

  if (body && method !== 'GET') {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(url, options);

  if (!response.ok) {
    const errorText = await response.text();
    console.error(`API Error: ${response.status} - ${errorText}`);
    
    if (errorText.includes('<!DOCTYPE') || errorText.includes('<html')) {
      throw new Error(`Received HTML instead of JSON. This usually means the API route is not properly configured. Status: ${response.status}`);
    }
    
    throw new Error(`API Error: ${response.status} - ${errorText.substring(0, 200)}`);
  }

  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const responseText = await response.text();
    console.error('Expected JSON but got:', contentType, responseText.substring(0, 200));
    throw new Error(`Expected JSON response but got ${contentType}`);
  }

  return await response.json();
};

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

export const validateResponse = (data: any, expectedField: string): boolean => {
  if (!data || typeof data !== 'object') {
    return false;
  }
  
  if (expectedField === 'value' && (!data.value || !Array.isArray(data.value))) {
    return false;
  }
  
  return true;
};

export const handleApiError = (error: any, context: string): string => {
  console.error(`[FRONTEND] Error in ${context}:`, error);
  
  if (error.name === 'AbortError') {
    return `Request timeout - ${context} took too long. This may indicate a large dataset.`;
  }
  
  return `Failed to load ${context}: ${error.message}`;
};